/**
 * Shared display configuration for user_tier_instances.
 *
 * Resolves the visual label / colour / icon to render for an instance based
 * on `play_mode` (the canonical contest-type axis). Centralised here so every
 * surface (dropdowns, dashboards, account pages, drafts dialogs) renders the
 * same chip.
 */

/**
 * Game-mode (cash = real-money contests, token = NT-only contests). When
 * present, game-mode drives the chip so users immediately see what kind of
 * contest the team is in. Until dedicated artwork lands, the icons here
 * piggyback on existing tier assets.
 */
export const GAME_MODE_CONFIG = {
  cash: {
    label: 'Cash Play',
    price: '€',
    color: 'bg-amber-500',
    textColor: 'text-amber-100',
    imagePath: '/assets/tiers/onyx_league.png'
  },
  token: {
    label: 'Token Play',
    price: 'Free',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-100',
    imagePath: '/assets/tiers/paper_league.png'
  }
} as const

/**
 * Fallback rendered when play_mode resolves to an unknown value (defensive
 * against future schema additions this map hasn't caught up to).
 */
export const FALLBACK_DISPLAY_CONFIG = {
  label: 'Team',
  price: '',
  color: 'bg-neu-surface-recessed',
  textColor: 'text-neu-text-primary',
  imagePath: '/assets/tiers/paper_league.png'
} as const

export type InstanceDisplayConfig = {
  label: string
  price: string
  color: string
  textColor: string
  imagePath: string
}

/**
 * Resolves the display config for an instance based on play_mode (the canonical
 * contest-type axis), falling back to a generic config for any unmapped values.
 */
export function getInstanceDisplayConfig(instance: {
  play_mode?: string | null
}): InstanceDisplayConfig {
  if (instance.play_mode && instance.play_mode in GAME_MODE_CONFIG) {
    return GAME_MODE_CONFIG[instance.play_mode as keyof typeof GAME_MODE_CONFIG]
  }
  return FALLBACK_DISPLAY_CONFIG
}
